/**
 * Pure helpers for the `filteredModules` and `mobileRailGrouped` memos in
 * layout/Sidebar.tsx.
 */

import type React from 'react';
import { MOBILE_RAIL_GROUPS } from '../constants';
import type { SidebarModuleItem } from '../types';

interface ComputeFilteredModulesArgs {
  tempModuleOrder: SidebarModuleItem[] | null;
  allModulesSorted: SidebarModuleItem[];
  normalizedSearchQuery: string;
}

/**
 * Dedupe by `module.id`, first occurrence wins. A drag-reorder snapshot
 * (`tempModuleOrder`) can end up carrying the same module twice — e.g. a
 * stale snapshot taken before a create/cancel settles — and the sidebar
 * renders this list keyed on `module.id` (#5542): a duplicate key makes React
 * lose child identity and drop click-driven renders for every module below
 * it. Returns the SAME array reference when nothing was duplicated so this
 * stays a no-op for the common case (no extra allocation, no memo churn).
 */
function dedupeModulesById(items: SidebarModuleItem[]): SidebarModuleItem[] {
  const seen = new Set<string>();
  let hasDuplicate = false;
  for (const item of items) {
    if (seen.has(item.module.id)) {
      hasDuplicate = true;
      break;
    }
    seen.add(item.module.id);
  }
  if (!hasDuplicate) return items;

  const kept = new Set<string>();
  const result: SidebarModuleItem[] = [];
  for (const item of items) {
    if (kept.has(item.module.id)) continue;
    kept.add(item.module.id);
    result.push(item);
  }
  return result;
}

export function computeFilteredModules({
  tempModuleOrder,
  allModulesSorted,
  normalizedSearchQuery,
}: ComputeFilteredModulesArgs): SidebarModuleItem[] {
  const source = dedupeModulesById(tempModuleOrder ?? allModulesSorted);
  if (normalizedSearchQuery) {
    return source.filter((item) => (item.displayName || '').toLowerCase().includes(normalizedSearchQuery));
  }
  return source;
}

export interface MobileRailGroup {
  key: string;
  label: string;
  types: string[];
  icon: React.ReactNode;
  modules: SidebarModuleItem[];
}

/** Group modules by rail type for mobile rail-style display. */
export function computeMobileRailGrouped(filteredModules: SidebarModuleItem[]): MobileRailGroup[] {
  const assigned = new Set<string>();
  return MOBILE_RAIL_GROUPS
    .map((group) => {
      const modules = filteredModules.filter((item) => {
        if (assigned.has(item.module.id)) return false;
        if (group.types.includes(item.module.type)) {
          assigned.add(item.module.id);
          return true;
        }
        return false;
      });
      // Pages: reverse to show newest first (API returns oldest-first by position)
      if (group.key === 'pages' && modules.length > 1) {
        modules.reverse();
      }
      return { ...group, modules };
    })
    .filter((g) => g.modules.length > 0);
}
